import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { fmtN, statusColor, posColor, posLabel } from './meta';

interface PosEntry {
  key: string;
  count: number;
}

interface IVocabularyStatsGridProps {
  total: number;
  learnedCount: number;
  activeCount: number;
  posBreakdown: PosEntry[];
}

const StatCard: React.FC<{
  value: string;
  label: string;
  sub?: string;
  accent?: string;
}> = ({ value, label, sub, accent }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 2,
      flex: 1,
      minWidth: 0,
      p: { xs: 1.5, md: '14px 18px' },
    }}
  >
    <Typography
      sx={{
        color: accent ?? 'text.primary',
        fontSize: { xs: 22, md: 28 },
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        color: 'text.secondary',
        fontSize: { xs: 12.5, md: 13.5 },
        mt: 0.75,
      }}
    >
      {label}
    </Typography>
    {sub && (
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 12.5,
          mt: 0.25,
          opacity: 0.85,
        }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

const PosDistribution: React.FC<{ entries: PosEntry[] }> = ({ entries }) => {
  const total = entries.reduce((s, p) => s + p.count, 0);
  if (!entries.length || !total) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 2,
          color: 'text.secondary',
          display: 'flex',
          fontSize: 13.5,
          justifyContent: 'center',
          minWidth: 0,
          p: 2,
        }}
      >
        {'Поки немає слів'}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minWidth: 0,
        p: { xs: 1.5, md: '14px 18px' },
      }}
    >
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.04em',
          mb: 1.25,
          textTransform: 'uppercase',
        }}
      >
        {'Розподіл за частинами мови'}
      </Typography>
      <Stack
        direction={'row'}
        sx={{
          borderRadius: 999,
          height: 12,
          mb: 1.5,
          overflow: 'hidden',
        }}
      >
        {entries.map((p) => (
          <Box
            key={p.key}
            title={`${posLabel(p.key)}: ${p.count}`}
            sx={{
              bgcolor: posColor(p.key),
              width: `${(p.count / total) * 100}%`,
            }}
          />
        ))}
      </Stack>
      <Stack
        direction={'row'}
        sx={{ flexWrap: 'wrap', gap: '7px 16px' }}
      >
        {entries.map((p) => (
          <Stack
            key={p.key}
            direction={'row'}
            alignItems={'center'}
            spacing={0.75}
          >
            <Box
              sx={{
                bgcolor: posColor(p.key),
                borderRadius: '3px',
                flex: 'none',
                height: 10,
                width: 10,
              }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {p.count}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>
              {posLabel(p.key)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Self-contained stats widget for vocabulary pages: 3 stat cards (total /
 * learned / active) + a part-of-speech distribution bar in a single grid.
 * Lives next to the title in PageIntro; hides when the page intro collapses.
 */
const VocabularyStatsGrid: React.FC<IVocabularyStatsGridProps> = ({
  total,
  learnedCount,
  activeCount,
  posBreakdown,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const learnedPct = total ? Math.round((learnedCount / total) * 100) : 0;
  const activePct = total ? Math.round((activeCount / total) * 100) : 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 1.25, md: 1.75 },
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr) 1.4fr',
        },
      }}
    >
      <StatCard
        value={fmtN(total)}
        label={'Усього слів'}
        sub={'у твоєму словнику'}
      />
      <StatCard
        value={fmtN(learnedCount)}
        label={'Вивчено'}
        sub={`${learnedPct}% словника`}
        accent={statusColor('LEARNED', mode)}
      />
      <StatCard
        value={fmtN(activeCount)}
        label={'В активному мовленні'}
        sub={`${activePct}% словника`}
        accent={statusColor('ACTIVE', mode)}
      />
      <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
        <PosDistribution entries={posBreakdown} />
      </Box>
    </Box>
  );
};

export default VocabularyStatsGrid;
